import React, { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { showToast } from "../../utils/toast";
import {
  useCreateCourseLearningOutcomeMutation,
  useGetAllCourseLearningOutcomeQuery,
  useUpdateCourseLearningOutcomeMutation,
} from "../../services/course/courseApi";
import { useDeleteCourseLearningOutcomeMutation } from "../../services/deletion-history/deletionHistoryApi";
import Loader from "../common/Loader";

const LearningOutcomesSection = ({ courseId }) => {
  const { data: learningOutcomeResponse } = useGetAllCourseLearningOutcomeQuery(
    courseId,
    {
      skip: !courseId,
    },
  );

  const [outcomes, setOutcomes] = useState([]);
  const [errorsByKey, setErrorsByKey] = useState({});
  const [originalById, setOriginalById] = useState({});
  const [createCourseLearningOutcome, { isLoading: isCreatingOutcome }] =
    useCreateCourseLearningOutcomeMutation();
  const [updateCourseLearningOutcome, { isLoading: isUpdatingOutcome }] =
    useUpdateCourseLearningOutcomeMutation();
  const [deleteCourseLearningOutcome, { isLoading: isDeletingOutcome }] =
    useDeleteCourseLearningOutcomeMutation();

  useEffect(() => {
    const responseData = learningOutcomeResponse?.data;

    if (!Array.isArray(responseData)) {
      setOutcomes([]);
      setOriginalById({});
      return;
    }

    const normalizedOutcomes = responseData?.map((item, index) => {
      return {
        _id: item?._id || "",
        outcomeDescription: item?.outcomeDescription || "",
      };
    });

    setOutcomes(normalizedOutcomes);
    setOriginalById(
      normalizedOutcomes.reduce((accumulator, item) => {
        accumulator[item._id] = item.outcomeDescription;
        return accumulator;
      }, {}),
    );
  }, [learningOutcomeResponse]);

  const handleOutcomeChange = (index, value) => {
    setOutcomes((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, outcomeDescription: value } : item,
      ),
    );
    const key = String(outcomes[index]?._id || index);
    setErrorsByKey((current) => {
      if (!current[key]) {
        return current;
      }
      const { [key]: _removed, ...rest } = current;
      return rest;
    });
  };

  const handleAddOutcome = () => {
    setOutcomes((current) => [
      ...current,
      { _id: `new-outcome-${Date.now()}`, outcomeDescription: "" },
    ]);
  };

  const handleCancelOutcome = (index) => {
    const key = String(outcomes[index]?._id || index);
    setOutcomes((current) =>
      current.filter((_, itemIndex) => itemIndex !== index),
    );
    setErrorsByKey((current) => {
      if (!current[key]) {
        return current;
      }
      const { [key]: _removed, ...rest } = current;
      return rest;
    });
  };

  const handleSaveOutcome = async (index) => {
    const outcome = outcomes[index];
    const trimmedValue = outcome?.outcomeDescription?.trim();
    const key = String(outcome?._id || index);

    if (!courseId) {
      return;
    }

    try {
      const response = await createCourseLearningOutcome({
        courseId,
        outcomeDescription: trimmedValue,
      }).unwrap();

      const createdId = response?.data?._id;

      setOutcomes((current) =>
        current.map((item, itemIndex) =>
          itemIndex === index
            ? {
                ...item,
                _id: createdId || item._id,
                outcomeDescription: trimmedValue,
              }
            : item,
        ),
      );

      if (createdId) {
        setOriginalById((current) => ({
          ...current,
          [createdId]: trimmedValue,
        }));
      }
      setErrorsByKey((current) => {
        if (!current[key]) {
          return current;
        }
        const { [key]: _removed, ...rest } = current;
        return rest;
      });
      showToast({
        message: "Learning outcome created successfully!",
        type: "success",
      });
    } catch (error) {
      const message =
        error?.data?.message ||
        error?.error ||
        "Failed to create learning outcome.";
      setErrorsByKey((current) => ({
        ...current,
        [key]: message,
      }));
      showToast({ message, type: "error" });
    }
  };

  const handleUpdateOutcome = async (index) => {
    const outcome = outcomes[index];
    const trimmedValue = outcome?.outcomeDescription?.trim();
    const key = String(outcome?._id || index);

    if (!courseId || !outcome?._id) {
      return;
    }

    try {
      const response = await updateCourseLearningOutcome({
        courseId,
        courseOutcomeId: outcome._id,
        outcomeDescription: trimmedValue,
      }).unwrap();
      setOriginalById((current) => ({
        ...current,
        [outcome._id]: trimmedValue,
      }));
      setErrorsByKey((current) => {
        if (!current[key]) {
          return current;
        }
        const { [key]: _removed, ...rest } = current;
        return rest;
      });
      showToast({
        message:
          response?.message || "Course learning outcome updated successfully.",
        type: "success",
      });
    } catch (error) {
      const message =
        error?.data?.message ||
        error?.error ||
        "Failed to update learning outcome.";
      setErrorsByKey((current) => ({
        ...current,
        [key]: message,
      }));
      showToast({ message, type: "error" });
    }
  };

  const handleRemoveOutcome = async (index) => {
    const outcome = outcomes[index];
    if (outcome?._id) {
      try {
        const response = await deleteCourseLearningOutcome({
          courseId,
          courseOutcomeId: outcome._id,
        }).unwrap();

        setOutcomes((current) =>
          current.filter((_, itemIndex) => itemIndex !== index),
        );

        showToast({
          message:
            response?.message ||
            "Course learning outcome deleted successfully.",
          type: "success",
        });
      } catch (error) {
        const message =
          error?.data?.message ||
          error?.error ||
          "Failed to delete learning outcome.";
        showToast({ message, type: "error" });
      }

      return;
    }

    setOutcomes((current) =>
      current.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  const requiredCount = 3;
  const filledCount = outcomes.filter(
    (o) => String(o?.outcomeDescription || "").trim().length > 0,
  ).length;

  const isComplete = filledCount >= requiredCount;

  return (
    <section className="mt-6 overflow-hidden rounded-xl border border-[#E5E7EB] bg-white shadow-[0_1px_2px_#0000000D]">
      <header className="flex flex-col gap-3 border-b border-[#E5E7EB] bg-[#F9FAFB] px-4 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:px-6">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-base text-[#1F2937] sm:text-lg">
            Learning Outcomes
          </h3>
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs ${
              isComplete
                ? "bg-[#ECFDF5] text-[#065F46]"
                : "bg-[#FEE2E2] text-[#991B1B]"
            }`}
          >
            {isComplete ? "Ready to publish" : "Minimum 3 required"}
          </span>
        </div>

      
      </header>

      <div className="p-4 sm:p-6">
        <p className="mb-3 text-sm text-[#374151]">
          What You&apos;ll Learn <span className="text-[#EF4444]">*</span>
          {!isComplete && (
            <span className="block text-xs text-[#6B7280] sm:ml-2 sm:inline">
              (Minimum {requiredCount} outcomes required)
            </span>
          )}
        </p>

        <div className="space-y-1.5">
          {outcomes.map((outcome, index) => {
            const key = String(outcome._id || index);
            const isNewOutcome = String(outcome._id || "").startsWith(
              "new-outcome-",
            );
            const isSystemOutcome = String(outcome._id || "").startsWith(
              "learning-outcome-",
            );
            const originalValue = outcome._id ? originalById[outcome._id] : "";
            const hasChanges =
              !isNewOutcome &&
              !isSystemOutcome &&
              outcome.outcomeDescription?.trim() !==
                (originalValue || "").trim();
            const errorMessage = errorsByKey[key];

            return (
              <div key={`outcome-${key}`}>
                <div className="flex min-h-[45px] flex-col gap-2 rounded-lg border border-[#D1D5DB] bg-white px-3 py-2 sm:flex-row sm:items-center">
                  <input
                    type="text"
                    value={outcome.outcomeDescription}
                    onChange={(event) =>
                      handleOutcomeChange(index, event.target.value)
                    }
                    placeholder="Add learning outcome"
                    className="min-h-8 w-full bg-transparent text-sm text-[#060607] outline-none placeholder:text-[#94A3B8] sm:text-base"
                  />

                  {isNewOutcome ? (
                    <div className="flex flex-wrap items-center gap-2 sm:ml-3 sm:flex-nowrap">
                      <button
                        type="button"
                        onClick={() => handleCancelOutcome(index)}
                        className="inline-flex h-8 flex-1 items-center justify-center rounded border border-[#D1D5DB] px-3 text-xs text-[#374151] transition hover:bg-[#F9FAFB] sm:flex-none"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveOutcome(index)}
                        className="inline-flex h-8 flex-1 items-center justify-center rounded border border-[#D1D5DB] px-3 text-xs text-[#374151] transition hover:bg-[#F9FAFB] sm:flex-none"
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-end gap-2 sm:ml-3">
                      {hasChanges ? (
                        <button
                          type="button"
                          onClick={() => handleUpdateOutcome(index)}
                          className="inline-flex h-8 items-center rounded border border-[#D1D5DB] px-3 text-xs text-[#374151] transition hover:bg-[#F9FAFB]"
                        >
                          Update
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => handleRemoveOutcome(index)}
                        className="text-[#9CA3AF] transition hover:text-[#6B7280]"
                        aria-label={`Remove outcome ${index + 1}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  )}
                </div>
                {errorMessage ? (
                  <p className="mt-1 text-xs text-[#DC2626]">{errorMessage}</p>
                ) : null}
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={handleAddOutcome}
          className="mt-2.5 inline-flex h-[34px] items-center rounded border border-[#D1D5DB] bg-white px-4 text-sm text-[#374151]"
        >
          + Add Outcome
        </button>
      </div>

      {(isDeletingOutcome || isCreatingOutcome || isUpdatingOutcome) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative z-10">
            <Loader size="lg" />
          </div>
        </div>
      )}
    </section>
  );
};

export default LearningOutcomesSection;
